import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { WebsitesService } from '../../core/services/websites.service';
import { combineLatest, filter, map, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { MatTabsModule } from '@angular/material/tabs';
import { WebsiteInformationComponent } from './components/website-information/website-information.component';
import { AsyncPipe } from '@angular/common';
import { SiteHistoryComponent } from './components/site-history/site-history.component';
import { SiteDiskUsageComponent } from './components/site-disk-usage/site-disk-usage.component';
import { GroupInformationComponent } from './components/group-information/group-information.component';
import { LoadingService } from '../../core/services/loading.service';
import { WPCliService } from '../../core/services/wp-cli.service';
import { MatDialog } from '@angular/material/dialog';
import { InstallWordpressDialogComponent } from './dialogs/install-wordpress-dialog/install-wordpress-dialog.component';
import { WebsocketService } from '../../core/services/websocket.service';
import { WPCheckResults } from '../../shared/models/wordpress.models';
import { WordpressInstalledDialogComponent } from './dialogs/wordpress-installed-dialog/wordpress-installed-dialog.component';
import { ConfirmationDialogComponent } from '../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { CreateStagingDialogComponent } from './dialogs/create-staging-dialog/create-staging-dialog.component';
import { AuthService } from '../../core/services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../../shared/components/snackbar/snackbar.component';
import { StatusModalService } from '../../core/services/status-modal.service';
import { AddAliasDialogComponent } from './dialogs/add-alias-dialog/add-alias-dialog.component';
import { AddSubdomainDialogComponent } from './dialogs/add-subdomain-dialog/add-subdomain-dialog.component';
import { WebsiteLogsService } from './services/website-logs.service';
import { LogsDialogComponent } from './dialogs/logs-dialog/logs-dialog.component';
import { SslCertificatesComponent } from './components/ssl-certificates/ssl-certificates.component';
import { CertificateService } from '../../core/services/certificate.service';
import { CdnVhostComponent } from './components/cdn-vhost/cdn-vhost.component';
import { RedisComponent } from './components/redis/redis.component';
import { RequestedLogs } from './models/requested-logs.model';
import { LogsPreviewDialogComponent } from './dialogs/logs-preview-dialog/logs-preview-dialog.component';
import { BandwidthComponent } from './components/bandwidth/bandwidth.component';
import { WebsiteStoreService } from '../../core/services/website-store.service';
import { RenameMainDialogComponent } from './dialogs/rename-main-dialog/rename-main-dialog.component';
import { WebsiteMigrationsService } from '../../core/services/website-migrations.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OriginVhostComponent } from './components/origin-vhost/origin-vhost.component';
import { PhpComponent } from './components/php/php.component';
import { DNSZoneComponent } from './components/dns-zone/dns-zone.component';
import { MailboxComponent } from './components/mailbox/mailbox.component';
import { DatabasesComponent } from './components/databases/databases.component';
import { FtpUsersComponent } from './components/ftp-users/ftp-users.component';
import { BackupsComponent } from './components/backups/backups.component';
import { RestoresComponent } from './components/restores/restores.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { UsageService } from '../../core/services/usage.service';
import moment from 'moment';

@Component({
  selector: 'app-website-overview',
  imports: [
    MatTabsModule,
    WebsiteInformationComponent,
    AsyncPipe,
    SiteHistoryComponent,
    SiteDiskUsageComponent,
    GroupInformationComponent,
    SslCertificatesComponent,
    CdnVhostComponent,
    RedisComponent,
    BandwidthComponent,
    OriginVhostComponent,
    PhpComponent,
    DNSZoneComponent,
    MailboxComponent,
    DatabasesComponent,
    FtpUsersComponent,
    BackupsComponent,
    RestoresComponent,
  ],
  templateUrl: './website-overview.component.html',
  styleUrl: './website-overview.component.scss',
})
export class WebsiteOverviewComponent implements OnInit, OnDestroy {
  private websitesService = inject(WebsitesService);

  private websiteStore = inject(WebsiteStoreService);

  private wpCLIService = inject(WPCliService);

  private websocketService = inject(WebsocketService);

  private loadingService = inject(LoadingService);

  private authService = inject(AuthService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  private websiteLogsService = inject(WebsiteLogsService);

  private certificateService = inject(CertificateService);

  private migrationsService = inject(WebsiteMigrationsService);

  private usageService = inject(UsageService);

  private router = inject(Router);

  private route = inject(ActivatedRoute);

  destroyed$ = new Subject();

  currentUser = this.authService.getUser()!;

  websiteData = this.websiteStore.websiteData;

  website = computed(() => this.websiteData()!.website);

  certificates$ = this.certificateService.searchCertificates(1, this.website().domain);

  readonly tabs = [
    'overview',
    'ssl',
    'cdn',
    'vhost',
    'dns',
    'email',
    'databases',
    'ftp-users',
    'backups',
    'restores',
  ] as const;

  activeTab = toSignal(
    this.route.queryParamMap.pipe(map((params) => params.get('tab') ?? 'overview')),
    { initialValue: 'details' },
  );

  selectedTabIndex = computed(() =>
    this.tabs.indexOf(this.activeTab() as (typeof this.tabs)[number]),
  );

  ngOnInit(): void {
    this.websocketService
      .listen<{ payload: { status: string } }>('websiteToggle')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        if (data.payload.status === 'done')
          this.websiteStore
            .loadWebsiteData(
              this.website().id,
              moment().utc().startOf('month').unix(),
              moment().utc().endOf('month').unix(),
            )
            .subscribe();
      });
  }

  onTabChange(index: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: this.tabs[index],
      },
    });
  }

  openWordpressDialog(): void {
    this.loadingService.show('Checking if WordPress is installed on this website');
    this.wpCLIService
      .getWPStatus(this.website().id)
      .pipe(
        switchMap(() =>
          this.websocketService.listen<{ payload: WPCheckResults }>('wpCheckResult').pipe(take(1)),
        ),
        tap(() => this.loadingService.hide()),
        switchMap((checkResult) =>
          !(checkResult.payload.wordpressStatus || checkResult.payload.otherWebsiteStatus)
            ? this.dialog
                .open(InstallWordpressDialogComponent, {
                  data: {
                    websiteId: this.website().id,
                    domain: this.website().domain,
                    forceInstall: false,
                  },
                })
                .afterClosed()
                .pipe(
                  switchMap((res) => {
                    if (res)
                      return this.wpCLIService.installWp(res).pipe(
                        tap(() =>
                          this.snackbar.openFromComponent(SnackbarComponent, {
                            data: {
                              text: `Installation of WordPress for ${this.website().domain} started`,
                              highlights: [this.website().domain],
                            },
                          }),
                        ),
                      );
                    else return of(res);
                  }),
                )
            : this.dialog
                .open(WordpressInstalledDialogComponent, {
                  data: { subTitle: this.website().domain },
                })
                .afterClosed()
                .pipe(
                  switchMap((res) => {
                    if (res?.delete) {
                      return this.dialog
                        .open(ConfirmationDialogComponent, {
                          data: {
                            title: 'Delete WordPress',
                            paragraphs: [
                              'Deleting WordPress will permanently remove all site content and the database.',
                              'Are you sure you want to delete this WordPress installation?',
                            ],
                            primaryButtonText: 'Delete WordPress',
                            type: 'delete',
                          },
                        })
                        .afterClosed()
                        .pipe(
                          switchMap((res) => {
                            if (res)
                              return this.wpCLIService.uninstallWp(this.website().id).pipe(
                                tap(() =>
                                  this.snackbar.openFromComponent(SnackbarComponent, {
                                    data: {
                                      text: 'Uninstalling WordPress',
                                    },
                                  }),
                                ),
                              );
                            return of(res);
                          }),
                        );
                    } else if (res?.install)
                      return this.dialog
                        .open(InstallWordpressDialogComponent, {
                          data: {
                            websiteId: this.website().id,
                            domain: this.website().domain,
                            forceInstall: true,
                          },
                        })
                        .afterClosed()
                        .pipe(
                          switchMap((res) => {
                            if (res)
                              return this.wpCLIService.installWp(res).pipe(
                                tap(() =>
                                  this.snackbar.openFromComponent(SnackbarComponent, {
                                    data: {
                                      text: `Installation of WordPress for ${this.website().domain} started`,
                                      highlights: [this.website().domain],
                                    },
                                  }),
                                ),
                              );
                            else return of(res);
                          }),
                        );
                    return of(res);
                  }),
                ),
        ),
        take(1),
      )
      .subscribe({
        next: (res) => {
          console.log(res);
        },
        error: (error) => {
          this.loadingService.hide();
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
        complete: () => this.loadingService.hide(),
      });
  }

  openStagingDialog(): void {
    this.dialog
      .open(CreateStagingDialogComponent, { data: { subTitle: this.website().domain } })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        tap((stagingData) => {
          if (stagingData.wpUsername && stagingData.wpPassword)
            this.wpCLIService.saveTempCredentials({
              domain: `staging.${this.website().domain}`,
              email: stagingData.wpEmail,
              user: stagingData.wpUsername,
              password: stagingData.wpPassword,
              enableRedisCache: false,
            });
        }),
        switchMap((data) =>
          this.websitesService.createWebsiteStaging({
            websiteId: this.website().id,
            websiteEnvironment: data.type,
            userId: this.currentUser.id,
          }),
        ),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Staging creation of ${this.website().domain} started`,
              highlights: [this.website().domain],
            },
          }),
        error: (error) => {
          if (error.status === 409)
            this.statusModalService.open({
              state: 'error',
              message: `Staging website for ${this.website().domain} already exists. To create new staging, delete the existing one first.`,
            });
          else
            this.statusModalService.open({
              state: 'error',
              message: error.error?.error,
            });
        },
      });
  }

  openAliasDialog(): void {
    this.dialog
      .open(AddAliasDialogComponent, {
        data: { subTitle: this.website().domain, websiteId: this.website().id },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        switchMap((data) =>
          combineLatest({
            create: this.websitesService.createWebsiteAlias(data),
            aliasName: of(data.domain),
          }),
        ),
      )
      .subscribe({
        next: ({ aliasName }) =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Adding alias site ${aliasName} started`,
              highlights: [aliasName],
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  openSubdomainDialog(): void {
    this.dialog
      .open(AddSubdomainDialogComponent, {
        data: {
          subTitle: this.website().domain,
          websiteId: this.website().id,
          domain: this.website().domain,
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        switchMap((data) =>
          combineLatest({
            create: this.websitesService.createSubwebsite(data),
            domainName: of(data.domain),
          }),
        ),
      )
      .subscribe({
        next: ({ domainName }) =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Adding subdomain ${domainName} started`,
              highlights: [domainName],
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  openLogsDialog(): void {
    this.dialog
      .open(LogsDialogComponent, {
        data: {
          subTitle: this.website().domain,
          websiteId: this.website().id,
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        switchMap((data) =>
          combineLatest({
            type: of(data.type),
            logs: this.websiteLogsService.requestLogs(data),
          }),
        ),
        tap(() => this.loadingService.show(`Gathering requested logs of ${this.website().domain}`)),
        switchMap(({ type }) =>
          combineLatest({
            type: of(type),
            payload: this.websocketService.listen<RequestedLogs>('requestLogs').pipe(take(1)),
          }),
        ),
      )
      .subscribe({
        next: ({ type, payload }) =>
          this.dialog.open(LogsPreviewDialogComponent, {
            data: { subTitle: this.website().domain, type, logs: payload.logs },
          }),
        complete: () => this.loadingService.hide(),
      });
  }

  toggleSiteStatus(status: 'ACTIVE' | 'DISABLED'): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: `${status === 'ACTIVE' ? 'Enable' : 'Disable'} Website`,
          paragraphs: [
            status === 'ACTIVE'
              ? `When this website is enabled, visitors will be able to access website ${this.website().domain} again.`
              : `When this website is disabled, visitors will see the default WPX page instead of ${this.website().domain} content.`,
            `Are you sure you want to ${status === 'ACTIVE' ? 'enable' : 'disable'} ${this.website().domain}?`,
          ],
          primaryButtonText: `${status === 'ACTIVE' ? 'Enable' : 'Disable'} Website`,
          icon: ['fas', status === 'ACTIVE' ? 'play' : 'pause'],
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        switchMap(() => this.websitesService.toggleWebsite(this.website().id, status)),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Website status change started`,
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  openRenameDialog(): void {
    this.dialog
      .open(RenameMainDialogComponent, { data: { websiteId: this.website().id } })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        switchMap((data) => this.migrationsService.renameWebsite(data)),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Website rename started`,
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  deleteWebsite(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Website',
          paragraphs: [
            `Deleting ${this.website().domain} will permanently remove the website, including its configuration, files, databases, and email accounts.`,
            `Are you sure you want to delete ${this.website().domain}`,
          ],
          primaryButtonText: 'Delete Website',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        switchMap(() => this.websitesService.deleteWebsite(this.website().id)),
      )
      .subscribe({
        next: () => {
          this.router.navigate(['/dashboard']);
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Website deletion started`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  syncWebsiteDiskUsage(): void {
    const groupId = this.websiteData()?.websiteGroup?.id;
    if (groupId)
      this.usageService.syncWebsiteGroupDiskUsageByGroupId(groupId).subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Website usage sync started`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
